/**
 * Sports Preferences & Recommendation Engine
 * Tracks user interactions and generates personalized game recommendations
 * Schema v2: tiered team/player follows with notification prefs
 */

const STORAGE_KEY = "sportsPreferences";
const MAX_HISTORY = 50;
const MAX_ALERT_HISTORY = 100;
const CURRENT_SCHEMA_VERSION = 2;

const makePlayerKey = (playerId, playerName) =>
  `${playerId}_${playerName}`.replace(/\s+/g, "_");

const migrateToV2 = (prefs) => {
  if (prefs.schemaVersion >= 2) return prefs;

  const teamFollows = {};

  (prefs.favoriteTeams || []).forEach((teamName) => {
    const meta = prefs.teamMetadata?.[teamName] || {};

    teamFollows[teamName] = {
      tier: "following",
      followedAt: prefs.lastUpdated || Date.now(),
      metadata: {
        name: teamName,
        logo: meta.logo || null,
        sport: meta.sport || null,
        league: meta.league || null,
      },
      notifications: {
        matchStart: true,
        scoreChange: true,
        finalScore: true,
      },
    };
  });

  prefs.schemaVersion = 2;
  prefs.teamFollows = teamFollows;
  prefs.playerFollows = {};
  prefs.alertHistory = [];

  return prefs;
};

/**
 * Get user's sports preferences from localStorage
 */
export const getSportsPreferences = () => {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (!stored) {
    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      favoriteTeams: [],
      favoriteSports: {},
      favoriteLeagues: {},
      viewHistory: [],
      teamFollows: {},
      playerFollows: {},
      alertHistory: [],
      lastUpdated: Date.now(),
    };
  }

  const parsed = JSON.parse(stored);
  const migrated = migrateToV2(parsed);

  if (parsed.schemaVersion !== migrated.schemaVersion) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
  }

  return migrated;
};

/**
 * Save sports preferences
 */
const saveSportsPreferences = (preferences) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
};

/**
 * Track when user clicks on a game
 */
export const trackGameView = (game) => {
  const prefs = getSportsPreferences();

  // Track sport preference
  const sport = game.genre || game.sport || "Unknown";

  prefs.favoriteSports[sport] = (prefs.favoriteSports[sport] || 0) + 1;

  // Track league preference
  const { league } = game;

  if (league) {
    prefs.favoriteLeagues[league] = (prefs.favoriteLeagues[league] || 0) + 1;
  }

  // Track team preferences
  if (game.homeTeam && !prefs.favoriteTeams.includes(game.homeTeam)) {
    // Increment team interest (we'll track in a separate object)
    if (!prefs.teamInterest) prefs.teamInterest = {};
    prefs.teamInterest[game.homeTeam] =
      (prefs.teamInterest[game.homeTeam] || 0) + 1;
  }
  if (game.awayTeam && !prefs.favoriteTeams.includes(game.awayTeam)) {
    if (!prefs.teamInterest) prefs.teamInterest = {};
    prefs.teamInterest[game.awayTeam] =
      (prefs.teamInterest[game.awayTeam] || 0) + 1;
  }

  // Auto-add teams as favorites if viewed 3+ times
  if (prefs.teamInterest) {
    Object.entries(prefs.teamInterest).forEach(([team, count]) => {
      if (count >= 3 && !prefs.favoriteTeams.includes(team)) {
        prefs.favoriteTeams.push(team);
      }
    });
  }

  // Add to view history (keep last 50)
  const gameId = game.id || game.mediaId;

  if (gameId) {
    prefs.viewHistory = [
      gameId,
      ...prefs.viewHistory.filter((id) => id !== gameId),
    ].slice(0, MAX_HISTORY);
  }

  prefs.lastUpdated = Date.now();
  saveSportsPreferences(prefs);
};

/**
 * Calculate relevance score for a game based on user preferences
 */
const calculateGameScore = (game, prefs) => {
  let score = 0;

  // Base score for live games
  if (game.rating === "LIVE") {
    score += 50;
  }

  // Sport preference (max 30 points)
  const sport = game.genre || game.sport;

  if (sport && prefs.favoriteSports[sport]) {
    const sportClicks = prefs.favoriteSports[sport];

    // More clicks = higher score, capped at 30
    score += Math.min(30, sportClicks * 5);
  }

  // League preference (max 25 points)
  if (game.league && prefs.favoriteLeagues[game.league]) {
    const leagueClicks = prefs.favoriteLeagues[game.league];

    score += Math.min(25, leagueClicks * 5);
  }

  // Favorite team involved (tiered: favorite=60, following=40)
  const homeTeamFavorite = prefs.favoriteTeams.includes(game.homeTeam);
  const awayTeamFavorite = prefs.favoriteTeams.includes(game.awayTeam);
  const homeTier = prefs.teamFollows?.[game.homeTeam]?.tier;
  const awayTier = prefs.teamFollows?.[game.awayTeam]?.tier;

  if (homeTier === "favorite" || awayTier === "favorite") {
    score += 60;
  } else if (homeTeamFavorite || awayTeamFavorite) {
    score += 40;
  }

  // Team interest (not favorite yet, but viewed before) (max 20 points)
  if (!homeTeamFavorite && prefs.teamInterest?.[game.homeTeam]) {
    score += Math.min(10, prefs.teamInterest[game.homeTeam] * 3);
  }
  if (!awayTeamFavorite && prefs.teamInterest?.[game.awayTeam]) {
    score += Math.min(10, prefs.teamInterest[game.awayTeam] * 3);
  }

  // Recent view history (slight boost if related teams/sport)
  // This helps with discovering similar games
  const recentlyViewedSimilar = prefs.viewHistory.slice(0, 10).some((id) =>
    // This is a simple check - in production you'd compare team/sport
    String(id).includes(sport)
  );

  if (recentlyViewedSimilar) {
    score += 5;
  }

  // Close game bonus (competitive score)
  const scoreDiff = Math.abs(
    (game.homeTeamScore || 0) - (game.awayTeamScore || 0)
  );

  if (game.rating === "LIVE" && scoreDiff <= 7) {
    score += 10; // Close game!
  }

  return score;
};

/**
 * Get personalized featured games
 * Returns top 4 games sorted by relevance to user
 */
export const getPersonalizedFeaturedGames = (allGames, count = 4) => {
  const prefs = getSportsPreferences();

  // If user has no preferences yet, prioritize live games first, then upcoming
  if (
    Object.keys(prefs.favoriteSports).length === 0 &&
    prefs.favoriteTeams.length === 0
  ) {
    // New user - show live games first, then by recency
    const liveGames = allGames.filter((g) => g.rating === "LIVE");
    const otherGames = allGames.filter((g) => g.rating !== "LIVE");

    return [...liveGames, ...otherGames].slice(0, count);
  }

  // Score all games
  const scoredGames = allGames.map((game) => ({
    game,
    score: calculateGameScore(game, prefs),
  }));

  // Sort by score (highest first)
  scoredGames.sort((a, b) => b.score - a.score);

  // Return top N games
  return scoredGames.slice(0, count).map((sg) => sg.game);
};

/**
 * Manually add/remove favorite team (backward-compatible wrapper)
 * @param {string|object} team - Team name or team object with metadata
 */
export const toggleFavoriteTeam = (team) => {
  const prefs = getSportsPreferences();
  const teamName = typeof team === "string" ? team : team.name;

  if (!prefs.teamMetadata) prefs.teamMetadata = {};

  if (prefs.favoriteTeams.includes(teamName)) {
    prefs.favoriteTeams = prefs.favoriteTeams.filter((t) => t !== teamName);
    delete prefs.teamFollows[teamName];
  } else {
    prefs.favoriteTeams.push(teamName);

    const meta =
      typeof team === "object" && team.logo
        ? {
            name: teamName,
            logo: team.logo,
            sport: team.sport,
            league: team.league,
          }
        : { name: teamName };

    if (typeof team === "object" && team.logo) {
      prefs.teamMetadata[teamName] = {
        ...meta,
        backdrop: team.backdrop || null,
      };
    }

    prefs.teamFollows[teamName] = {
      tier: "following",
      followedAt: Date.now(),
      metadata: meta,
      notifications: {
        matchStart: true,
        scoreChange: true,
        finalScore: true,
      },
    };
  }

  saveSportsPreferences(prefs);

  // Always return a fresh array so callers using setState() get a new
  // reference and React schedules a re-render even on add (push mutates).
  return [...prefs.favoriteTeams];
};

/**
 * Get user's favorite teams
 */
export const getFavoriteTeams = () => [
  ...(getSportsPreferences().favoriteTeams || []),
];

/**
 * Get stored team metadata
 */
export const getTeamMetadata = (teamName) => {
  const prefs = getSportsPreferences();

  return prefs.teamMetadata?.[teamName] || null;
};

/**
 * Get all stored team metadata
 */
export const getAllTeamMetadata = () => {
  const prefs = getSportsPreferences();

  return prefs.teamMetadata || {};
};

/**
 * Clear all sports preferences (useful for testing or reset)
 */
export const clearSportsPreferences = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Get top sports by user interest
 */
export const getTopSports = (limit = 3) => {
  const prefs = getSportsPreferences();
  const sports = Object.entries(prefs.favoriteSports)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([sport]) => sport);

  return sports;
};

// ─── V2: Tiered Team Follow Functions ────────────────────────────────

export const followTeam = (team, tier = "following") => {
  if (!team) return getSportsPreferences().favoriteTeams || [];
  const prefs = getSportsPreferences();
  const teamName = typeof team === "string" ? team : team?.name;

  if (!teamName) return prefs.favoriteTeams || [];
  const meta =
    typeof team === "object"
      ? {
          name: teamName,
          logo: team.logo || null,
          sport: team.sport || null,
          league: team.league || null,
        }
      : { name: teamName };

  prefs.teamFollows[teamName] = {
    tier,
    followedAt: Date.now(),
    metadata: meta,
    notifications: {
      matchStart: true,
      scoreChange: true,
      finalScore: true,
    },
  };

  if (!prefs.favoriteTeams.includes(teamName)) {
    prefs.favoriteTeams.push(teamName);
  }

  if (!prefs.teamMetadata) prefs.teamMetadata = {};
  if (typeof team === "object" && team.logo) {
    prefs.teamMetadata[teamName] = { ...meta, backdrop: team.backdrop || null };
  }

  saveSportsPreferences(prefs);

  return prefs.favoriteTeams;
};

export const unfollowTeam = (teamName) => {
  const prefs = getSportsPreferences();

  delete prefs.teamFollows[teamName];
  prefs.favoriteTeams = prefs.favoriteTeams.filter((t) => t !== teamName);
  saveSportsPreferences(prefs);

  return prefs.favoriteTeams;
};

export const setTeamTier = (teamName, tier) => {
  const prefs = getSportsPreferences();

  if (prefs.teamFollows[teamName]) {
    prefs.teamFollows[teamName].tier = tier;
    saveSportsPreferences(prefs);
  }
};

export const isTeamFollowed = (teamName) => {
  const prefs = getSportsPreferences();
  const entry = prefs.teamFollows[teamName];

  return entry
    ? { followed: true, tier: entry.tier }
    : { followed: false, tier: null };
};

export const getFollowedTeamNames = () => {
  const prefs = getSportsPreferences();

  return Object.keys(prefs.teamFollows || {});
};

export const getFavoriteTeamNames = () => {
  const prefs = getSportsPreferences();

  return Object.entries(prefs.teamFollows || {})
    .filter(([, v]) => v.tier === "favorite")
    .map(([k]) => k);
};

export const setTeamNotificationPref = (teamName, key, value) => {
  const prefs = getSportsPreferences();

  if (prefs.teamFollows[teamName]) {
    prefs.teamFollows[teamName].notifications[key] = value;
    saveSportsPreferences(prefs);
  }
};

export const getTeamFollowEntry = (teamName) => {
  const prefs = getSportsPreferences();

  return prefs.teamFollows?.[teamName] || null;
};

// ─── V2: Player Follow Functions ─────────────────────────────────────

export const followPlayer = (player) => {
  const prefs = getSportsPreferences();
  const key = makePlayerKey(
    player.id || player.idPlayer,
    player.name || player.strPlayer
  );

  prefs.playerFollows[key] = {
    tier: "following",
    followedAt: Date.now(),
    metadata: {
      id: player.id || player.idPlayer,
      name: player.name || player.strPlayer,
      photo: player.photo || player.strCutout || player.strThumb || null,
      position: player.position || player.strPosition || null,
      teamName: player.teamName || player.strTeam || null,
      sport: player.sport || null,
      league: player.league || null,
    },
    notifications: { goalOrAssist: true, startingLineup: true },
  };

  saveSportsPreferences(prefs);

  return key;
};

export const unfollowPlayer = (playerId, playerName) => {
  const prefs = getSportsPreferences();
  const key = makePlayerKey(playerId, playerName);

  delete prefs.playerFollows[key];
  saveSportsPreferences(prefs);
};

export const setPlayerTier = (playerKey, tier) => {
  const prefs = getSportsPreferences();

  if (prefs.playerFollows[playerKey]) {
    prefs.playerFollows[playerKey].tier = tier;
    saveSportsPreferences(prefs);
  }
};

export const isPlayerFollowed = (playerId, playerName) => {
  const prefs = getSportsPreferences();
  const key = makePlayerKey(playerId, playerName);
  const entry = prefs.playerFollows[key];

  return entry
    ? { followed: true, tier: entry.tier, key }
    : { followed: false, tier: null, key };
};

export const getFollowedPlayers = () => {
  const prefs = getSportsPreferences();

  return Object.entries(prefs.playerFollows || {}).map(([key, value]) => ({
    key,
    ...value,
  }));
};

export const getFollowedPlayersByTeam = (teamName) =>
  getFollowedPlayers().filter((p) => p.metadata?.teamName === teamName);

export const setPlayerNotificationPref = (playerKey, key, value) => {
  const prefs = getSportsPreferences();

  if (prefs.playerFollows[playerKey]) {
    prefs.playerFollows[playerKey].notifications[key] = value;
    saveSportsPreferences(prefs);
  }
};

// ─── V2: League Follow Functions ─────────────────────────────────────

export const followLeague = (league, tier = "following") => {
  const prefs = getSportsPreferences();
  const leagueId =
    typeof league === "string" ? league : league.id || league.idLeague;
  const meta =
    typeof league === "object"
      ? {
          id: leagueId,
          name: league.name || league.strLeague || leagueId,
          badge: league.badge || league.strBadge || null,
          sport: league.sport || league.strSport || null,
          country: league.country || league.strCountry || null,
        }
      : { id: leagueId, name: leagueId };

  if (!prefs.leagueFollows) prefs.leagueFollows = {};
  prefs.leagueFollows[leagueId] = {
    tier,
    followedAt: Date.now(),
    metadata: meta,
  };

  saveSportsPreferences(prefs);

  return prefs.leagueFollows;
};

export const unfollowLeague = (leagueId) => {
  const prefs = getSportsPreferences();

  if (!prefs.leagueFollows) return {};
  delete prefs.leagueFollows[leagueId];
  saveSportsPreferences(prefs);

  return prefs.leagueFollows;
};

export const isLeagueFollowed = (leagueId) => {
  const prefs = getSportsPreferences();
  const entry = prefs.leagueFollows?.[leagueId];

  return entry
    ? { followed: true, tier: entry.tier }
    : { followed: false, tier: null };
};

export const getFollowedLeagues = () => {
  const prefs = getSportsPreferences();

  return Object.entries(prefs.leagueFollows || {}).map(([key, value]) => ({
    id: key,
    ...value,
  }));
};

export const setLeagueTier = (leagueId, tier) => {
  const prefs = getSportsPreferences();

  if (prefs.leagueFollows?.[leagueId]) {
    prefs.leagueFollows[leagueId].tier = tier;
    saveSportsPreferences(prefs);
  }
};

// ─── V2: Alert History ───────────────────────────────────────────────

export const recordAlert = (alertId) => {
  const prefs = getSportsPreferences();

  if (!prefs.alertHistory) prefs.alertHistory = [];
  prefs.alertHistory = [alertId, ...prefs.alertHistory].slice(
    0,
    MAX_ALERT_HISTORY
  );
  saveSportsPreferences(prefs);
};

export const hasAlertBeenShown = (alertId) => {
  const prefs = getSportsPreferences();

  return (prefs.alertHistory || []).includes(alertId);
};

// ─── V2: Personalized Ordering ──────────────────────────────────────

export const getPersonalizedLiveNowOrder = (allGames) => {
  const prefs = getSportsPreferences();
  const favoriteTeams = getFavoriteTeamNames();
  const followedTeamNames = getFollowedTeamNames();
  const followedPlayerTeams = new Set(
    getFollowedPlayers()
      .map((p) => p.metadata?.teamName)
      .filter(Boolean)
  );

  const scored = allGames.map((game) => {
    let s = 0;
    const homeMatch = game.homeTeam;
    const awayMatch = game.awayTeam;

    if (favoriteTeams.includes(homeMatch) || favoriteTeams.includes(awayMatch))
      s += 100;
    else if (
      followedTeamNames.includes(homeMatch) ||
      followedTeamNames.includes(awayMatch)
    )
      s += 70;
    else if (
      followedPlayerTeams.has(homeMatch) ||
      followedPlayerTeams.has(awayMatch)
    )
      s += 40;

    const sport = game.genre || game.sport;

    if (sport && prefs.favoriteSports[sport])
      s += Math.min(20, prefs.favoriteSports[sport] * 3);

    const diff = Math.abs(
      (game.homeTeamScore || 0) - (game.awayTeamScore || 0)
    );

    if (game.rating === "LIVE" && diff <= 7) s += 10;

    return { game, score: s };
  });

  scored.sort((a, b) => b.score - a.score);

  return scored.map((s) => s.game);
};

export const getBecauseYouFollowRails = (allGames) => {
  const followed = getFollowedTeamNames();
  const prefs = getSportsPreferences();
  const rails = [];

  for (const teamName of followed.slice(0, 3)) {
    const entry = prefs.teamFollows[teamName];
    const games = allGames.filter(
      (g) => g.homeTeam === teamName || g.awayTeam === teamName
    );

    if (games.length > 0) {
      rails.push({
        label: `Because you follow ${teamName}`,
        entityName: teamName,
        logo: entry?.metadata?.logo || null,
        games: games.slice(0, 6),
      });
    }
  }

  return rails;
};

export const getColdStartRecommendations = (allGames) => {
  const live = allGames.filter((g) => g.rating === "LIVE");
  const upcoming = allGames.filter((g) => g.rating !== "LIVE");

  return [...live, ...upcoming].slice(0, 12);
};

const sportsPreferences = {
  getSportsPreferences,
  trackGameView,
  getPersonalizedFeaturedGames,
  toggleFavoriteTeam,
  getFavoriteTeams,
  getTeamMetadata,
  getAllTeamMetadata,
  clearSportsPreferences,
  getTopSports,
  followTeam,
  unfollowTeam,
  setTeamTier,
  isTeamFollowed,
  getFollowedTeamNames,
  getFavoriteTeamNames,
  setTeamNotificationPref,
  getTeamFollowEntry,
  followPlayer,
  unfollowPlayer,
  setPlayerTier,
  isPlayerFollowed,
  getFollowedPlayers,
  getFollowedPlayersByTeam,
  setPlayerNotificationPref,
  recordAlert,
  hasAlertBeenShown,
  getPersonalizedLiveNowOrder,
  getBecauseYouFollowRails,
  getColdStartRecommendations,
  followLeague,
  unfollowLeague,
  isLeagueFollowed,
  getFollowedLeagues,
  setLeagueTier,
};

export default sportsPreferences;
